import React from "react";
import {cl,globs} from "../../components/utils/utils";
import C18MuiInput from '../../usa/components/C18MuiInput'
import C18MuiInputLabel from '../../usa/components/C18MuiInputLabel'
import C18MuiSelect from '../../usa/components/C18MuiSelect'
import C18MuiMenuItem from '../../usa/components/C18MuiMenuItem'

/* Coordinate control (UI-only).
 * Renders a *positive* magnitude input plus a hemisphere dropdown, but still
 * loads / stores a *signed* decimal-degree value so the backend format is
 * completely unchanged.
 *   Latitude : North = +, South = -   (pid 4735)
 *   Longitude: East  = +, West  = -   (pid 4737)
 * This is the standard geographic sign convention and matches what the live
 * controller actually displays, per the firmware function
 * GetCoordinateHemisphere_Str in landru-mainboard/Menus/src/Menu_Utilities.c
 * (Latitude<0 => South, Longitude<0 => West). Verified on-device: a stored
 * latitude of -35 shows "35 South" and a stored longitude of -117 shows
 * "117 West" on both the controller and this UI.
 * NOTE: the firmware team initially described longitude as "West positive",
 * which conflicts with the controller's own display (and its SetCoordinate-
 * Hemisphere_Str appears inconsistent with GetCoordinateHemisphere_Str). We
 * align to the controller display; revisit if firmware standardizes this.
 */
class Coordinate extends React.Component{
    constructor(props) {
        super(props);
//         cl(props)
        this.props.getValue(props.ind, {type: "scalar"})
        this.axis = (props.axis === "lat") ? "lat" : "lng"
        let c = props.custom || {}
        // magnitude bounds derived from the original signed customProps range
        this.maxMag = (("max" in c) && !isNaN(c.max)) ? Math.abs(Number(c.max))
            : (this.axis === "lat" ? 90 : 180)
        this.step = (("step" in c) && !isNaN(c.step)) ? Number(c.step) : 1
        this.decimal = (("decimal" in c) && !isNaN(c.decimal)) ? Number(c.decimal) : 0
        this.unit = "°"
        // direction options, positive hemisphere first. Standard sign convention,
        // matching the controller display: Lat North=+, South=-; Long East=+, West=-.
        this.options = (this.axis === "lat")
            ? [{v: 1, t: "North"}, {v: -1, t: "South"}]
            : [{v: 1, t: "East"}, {v: -1, t: "West"}]
        // hemisphere to show while the magnitude is exactly 0 (no sign to read).
        // +1 => North / East, the positive default.
        this.zeroDir = 1
        this.changed = false
        this.state = {}
        this.subscribe_updateFui = globs.events.subscribe("updateFui", this.updateFui)
        this.subscribe_savePageEvent = globs.events.subscribe("savePageEvent", this.savePageEvent)
    }

    componentWillUnmount = () => {
        this.subscribe_updateFui.remove()
        this.subscribe_savePageEvent.remove()
    }

    updateFui = () => {
        if (!this.changed) {this.props.getValue(this.props.ind, {type: "scalar"})}
    }

    savePageEvent = () => {
        this.changed = false
    }

    // decompose the current signed value into magnitude + hemisphere sign
    parseValue = () => {
        let num = Number(this.props.value)
        if (isNaN(num)) {num = 0}
        let sign = (num < 0) ? -1 : (num > 0 ? 1 : this.zeroDir)
        return {num: num, mag: Math.abs(num), sign: sign}
    }

    // build the hemisphere dropdown <option> list
    makeDirectionOptions = () => {
        return this.options.map((o, i) => {
            return (
                <C18MuiMenuItem key={i} value={o.v}>{o.t}</C18MuiMenuItem>
            )
        })
    }

    // recombine magnitude + hemisphere sign into a single signed scalar and
    // push it up exactly like FUINumerical does (backend format unchanged)
    commit = (sign, mag) => {
        if (isNaN(mag)) {mag = 0}
        let obj = {type: "scalar", value: sign * mag}
        this.props.onChange(this.props.ind, obj)
        this.changed = true // blocks updating
    }

    onNumChange = e => {
        let {sign} = this.parseValue()
        this.commit(sign, parseFloat(e.target.value))
    }

    onNumBlur = e => {
        let {sign} = this.parseValue()
        let mag = parseFloat(e.target.value)
        if (isNaN(mag)) {mag = 0}
        mag = Math.abs(mag)                       // never allow a negative magnitude
        if (mag > this.maxMag) {mag = this.maxMag} // clamp to axis range on blur
        this.commit(sign, mag)
    }

    onDirChange = e => {
        let sign = Number(e.target.value)
        let {mag} = this.parseValue()
        if (mag === 0) {this.zeroDir = sign} // remember choice while there is no sign to store
        this.commit(sign, mag)
    }

    render() {
        let pr = this.props
        let {mag, sign} = this.parseValue()
        let magDisp = Math.round(mag * 10000) / 10000 // round to 4 decimal, mirror FUINumerical
        return (
            <div style={{marginBottom: 0}}>
                <C18MuiInputLabel htmlFor="coordinate">
                    {pr.cont.title}
                </C18MuiInputLabel>
                <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", rowGap: 8}}>
                    <C18MuiInput
                        id={`{"cmd":"watch","type":"${pr.type}","id":${pr.cont.pid},"zuci":"${pr.zuci}"}`}
                        title={(this.props?.rest?.parent || (e => {}))({uri: "pid", pr: pr})}
                        key="coordinate"
                        margin="dense"
                        value={magDisp}
                        onChange={this.onNumChange}
                        onBlur={this.onNumBlur}
                        saveOK={pr.saveOK}
                        saved={this.changed}
                        type="tel"
                        unit={this.unit}
                        noBreak={true}
                        inputProps={{
                            step: this.step,
                            min: 0,
                            max: this.maxMag,
                            inputMode: "decimal",
                            type: "tel",
                            style: {textAlign: 'center'}
                        }}
                    />
                    <div style={{marginBottom: 0}}>
                        <C18MuiSelect
                            value={sign}
                            title={(this.props?.rest?.parent || (e => {}))({uri: "pid", pr: pr})}
                            onChange={this.onDirChange}
                            saveOK={pr.saveOK}
                            break="none"
                        >
                            {this.makeDirectionOptions()}
                        </C18MuiSelect>
                    </div>
                </div>
            </div>
        );
    }
}

export default Coordinate;
